// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import * as Actions from "../actions"
import * as Slice from "../slice"
import * as TimelineActions from "../../timeline/actions"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import {
  CreateTimesheetFieldOptionRequest,
  ListTimesheetFieldOptionsRequest,
  ListTimesheetFieldsRequest,
  TimesheetFieldOption,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type CreateTimesheetFieldRpc = (request: CreateTimesheetFieldOptionRequest) => Observable<TimesheetFieldOption>

export const createTimesheetFieldOption = (createTimesheetFieldOptionRpc: CreateTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createTimesheetFieldOption.match),
      map(action => action.payload),
      mergeMap(payload =>
        createTimesheetFieldOptionRpc(payload.request).pipe(
          mergeMap((response) => {
            if (payload.onSuccess) {
              payload.onSuccess()
            }
            return of(Slice.addTimesheetFieldOption({
              option: response.toObject(),
              timesheetFieldId: payload.request.getFieldId(),
            }))
          }),
          catchError(e => handleError(e, payload)),
        )
      ),
    )

const handleError = (e: unknown, payload: Actions.Payload<CreateTimesheetFieldOptionRequest>): Observable<Action> => {
  if (payload.onError) {
    payload.onError()
  }
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat([
        TimelineActions.listTimesheetFields({
          request: new ListTimesheetFieldsRequest().setSiteId(payload.request.getSiteId()),
          includeOptions: true,
        }),
        showSnackBar({
          message: t({
            id: "msg_timesheet_field_not_found",
            message: "Timesheet field not found.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat([
        Actions.listTimesheetFieldOptions(
          new ListTimesheetFieldOptionsRequest()
            .setSiteId(payload.request.getSiteId())
            .setTimesheetFieldId(payload.request.getFieldId())
        ),
        showSnackBar({
          message: t({
            id: "msg_create_timesheet_field_option_failed_precondition",
            message: "Failed to create timesheet field option. Duplicate label or value.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({
          id: "msg_create_timesheet_field_option_permission_denied",
          message: "Failed to create timesheet field option. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_create_timesheet_field_option_failed",
      message: "Failed to create timesheet field option. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const createTimesheetFieldOptionRpc = (request: CreateTimesheetFieldOptionRequest): Observable<TimesheetFieldOption> =>
  grpc(() => timesheetFieldClient.createTimesheetFieldOption(request, null))

export default createTimesheetFieldOption(createTimesheetFieldOptionRpc)
